import styled, {css} from "styled-components";
import {useEffect, useRef} from "react";
import {colorPalette} from "../styles/colorPalette";
import {baseDistance, ELLIPSIS, FlexCenterStyle, fontSize, fontWeight, HEADER_HEIGHT, navBarZIndex, radius, zIndex} from "../styles/styles";
import {useAppStore} from "../../../StateManager";
import {IconButtonStyled, IconButtonV5} from "../interaction/IconButtonV5";
import PlusIcon from "../icons/circle-plus.svg?react";
import DotsHorizontalIcon from "../icons/dots-horizontal.svg?react";
import {DropdownButtonStyled, DropdownButtonV5} from "../interaction/DropdownButtonV5";
import {Functions} from "../../../utils/function/Functions";
import {useClickOutside} from "../utils";
import {ProjectNameDropDownStyled} from "../topbar/ProjectName.styled";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import type {TMenuV5} from "./NavigationCommon";
import {ClassNameOfViewPopout} from "./FloatingMenu";
import {ViewElementStyled} from "./views/WorkspaceViewCommon";

interface IMenuStyled {
	$onlyIcon?: boolean;
	$small?: boolean;
	$active?: boolean;
	$home?: boolean;
}

export const NavigationMenuItem = (props: TMenuV5) => {
	const appState = useAppStore((state) => state.appState);
	const ref = useRef<HTMLDivElement>(null);
	const floatingMenuRef = useRef<HTMLDivElement>(null);
	const modalContainerRef = useRef<HTMLDivElement>(null);
	const {isActive, label, onlyIcon, small, floatingMenu, dropdownOptions, closeFloatingMenu, onPlusClick, icon: Icon} = props;

	useEffect(() => {
		modalContainerRef.current = appState.app.modalContainer;
	}, [appState.app.modalContainer]);

	const isHome = appState.selectedMenu === "home";

	const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		props.onClick?.();
	};

	useClickOutside([ref, floatingMenuRef, modalContainerRef], closeFloatingMenu ? closeFloatingMenu : Functions.emptyFunction);

	const renderTitleRow = (renderButtons: boolean) => {
		return (
			<>
				<Icon />
				<h4>{label}</h4>
				{renderButtons && props.onPlusClick && (
					<IconButtonV5
						onClick={onPlusClick}
						IconComponent={PlusIcon}
					/>
				)}
				{renderButtons && dropdownOptions?.length > 0 && (
					<DropdownButtonV5
						button={
							<IconButtonV5
								IconComponent={DotsHorizontalIcon}
								onClick={Functions.emptyFunction}
							/>
						}
						options={dropdownOptions}
						optionsZIndex={navBarZIndex + 2}
					/>
				)}
			</>
		);
	};

	return (
		<NavigationMenuItemStyled
			className={label}
			$active={isActive}
			$onlyIcon={onlyIcon}
			$home={isHome}
			$small={small}
			onClick={onClick}
			ref={ref}
		>
			{renderTitleRow(!onlyIcon)}
			{floatingMenu && (
				<DomPortal destination={appState.app.modalContainer}>
					<FloatingMenuStyled
						ref={floatingMenuRef}
						className={ClassNameOfViewPopout}
					>
						<TitleRowStyled>{renderTitleRow(true)}</TitleRowStyled>
						{floatingMenu}
					</FloatingMenuStyled>
				</DomPortal>
			)}
		</NavigationMenuItemStyled>
	);
};

export const TitleRowStyled = styled.div`
	display: flex;
	align-items: center;
	gap: ${baseDistance.xs};
	padding: ${baseDistance.xs};

	h4 {
		font-size: ${fontSize.lg};
		font-weight: ${fontWeight.normal};
		${ELLIPSIS};
		flex: 1;
	}

	${IconButtonStyled} {
		min-width: 24px;
		max-width: 24px;
		height: 24px;

		svg {
			width: 16px;
			height: 16px;
		}
	}

	${DropdownButtonStyled} {
		min-width: 24px;
		height: 24px;
	}

	margin-bottom: ${baseDistance.sm};
`;

export const FloatingMenuStyled = styled.div`
	position: absolute;
	left: 64px;
	top: ${HEADER_HEIGHT};
	width: 336px;
	height: calc(100% - 65px);
	background-color: ${colorPalette.blueGray.c100};
	padding: ${baseDistance.sm};
	overflow-y: auto;
	z-index: 11;

	${ProjectNameDropDownStyled} {
		border: 1px solid ${colorPalette.gray.c300};
	}
`;

const NavigationMenuItemStyled = styled.div<IMenuStyled>`
	${FlexCenterStyle}
	cursor: pointer;
	gap: ${baseDistance.sm};
	padding: ${baseDistance.xs};
	position: relative;
	border-radius: ${radius.sm};
	height: 24px;
	min-width: 24px;
	line-height: 24px;

	&.Favorites {
		> svg {
			fill: #f5d254;

			path {
				stroke: #f5d254;
			}
		}
	}

	${FloatingMenuStyled} {
		${ViewElementStyled} {
			white-space: nowrap;
			margin-left: initial;
		}
	}

	> svg {
		width: 16px;
		height: 16px;
	}

	${(props) =>
		props.$active &&
		css`
			background-color: ${colorPalette.primary.c500Primary};
			color: ${colorPalette.white};

			h4 {
				background-color: ${colorPalette.primary.c500Primary};
				color: ${colorPalette.white};
			}

			svg path {
				stroke: ${colorPalette.white};
			}
		`};

	${(props) =>
		!props.$active &&
		css`
			&:hover {
				background-color: ${colorPalette.blueGray.c200Light};
				color: ${colorPalette.gray.c950};
			}
		`};

	${IconButtonStyled} {
		min-width: 24px;
		max-width: 24px;
		height: 24px;

		svg {
			width: 16px;
			height: 16px;
		}
	}

	${DropdownButtonStyled} {
		min-width: 24px;
		height: 24px;
	}

	h4 {
		font-size: ${fontSize.md};
		font-weight: ${fontWeight.normal};
		${ELLIPSIS};
		flex: 1;
	}

	${(props) =>
		props.$onlyIcon &&
		css`
			align-items: center;

			&:hover {
				h4 {
					height: 32px;
					padding: 0 ${baseDistance.sm};
					${FlexCenterStyle};
				}
			}

			h4 {
				position: absolute;
				background-color: ${props.$active ? colorPalette.primary.c500Primary : colorPalette.blueGray.c200Light};
				color: ${props.$active ? colorPalette.white : colorPalette.gray.c950};
				border-radius: ${radius.sm};
				left: 38px;
				display: none;
				z-index: ${zIndex.notification};
			}
		`};
`;
